import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { UserRound, LockKeyhole, ShieldCheck } from "lucide-react";

export default function AccountSettings() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingPin, setSavingPin] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [originalEmail, setOriginalEmail] = useState("");
  const [profilePassword, setProfilePassword] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [pinPassword, setPinPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [hasPin, setHasPin] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/auth/account");
        setName(data.name || "");
        setEmail(data.email || "");
        setOriginalEmail((data.email || "").toLowerCase());
        setHasPin(!!data.has_pin);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat pengaturan akun");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = { name: name.trim(), email: email.trim() };
      if (email.trim().toLowerCase() !== originalEmail) {
        payload.current_password = profilePassword;
      }

      const { data } = await api.patch("/auth/account", payload);
      setName(data.name || "");
      setEmail(data.email || "");
      setOriginalEmail((data.email || "").toLowerCase());
      setProfilePassword("");
      await refresh();
      toast.success("Pengaturan akun berhasil disimpan");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menyimpan pengaturan akun");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Konfirmasi password baru tidak sama");
      return;
    }

    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("Password berhasil diubah. Silakan login kembali.");
      setTimeout(() => navigate("/login", { replace: true }), 500);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal mengubah password");
    } finally {
      setSavingPassword(false);
    }
  };

  const savePin = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(newPin)) {
      toast.error("PIN harus 6 digit angka");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("Konfirmasi PIN tidak sama");
      return;
    }

    setSavingPin(true);
    try {
      const { data } = await api.post("/auth/change-pin", {
        current_password: pinPassword,
        new_pin: newPin,
        confirm_pin: confirmPin,
      });
      setHasPin(!!data.has_pin);
      setPinPassword("");
      setNewPin("");
      setConfirmPin("");
      await refresh();
      toast.success("PIN keamanan berhasil disimpan");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menyimpan PIN keamanan");
    } finally {
      setSavingPin(false);
    }
  };

  if (loading) {
    return <div className="p-2 text-sm text-zinc-500">Memuat pengaturan akun...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Pengaturan Akun</h1>
        <p className="text-sm text-zinc-500 mt-1">Kelola identitas Super Admin, password, dan PIN keamanan.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserRound size={18} /> Profil</CardTitle>
          <CardDescription>Ubah nama dan email akun Super Admin.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4 max-w-xl">
            <div className="space-y-1.5">
              <Label htmlFor="account-name">Nama</Label>
              <Input id="account-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="account-email">Email</Label>
              <Input id="account-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="profile-password">Password saat ini</Label>
              <Input
                id="profile-password"
                type="password"
                value={profilePassword}
                onChange={(e) => setProfilePassword(e.target.value)}
                placeholder="Wajib diisi bila email berubah"
              />
            </div>

            <Button type="submit" disabled={savingProfile} className="bg-emerald-600 hover:bg-emerald-700">
              {savingProfile ? "Menyimpan..." : "Simpan Profil"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><LockKeyhole size={18} /> Ganti Password</CardTitle>
          <CardDescription>Gunakan password lama, password baru, dan konfirmasi password baru.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={savePassword} className="space-y-4 max-w-xl">
            <div className="space-y-1.5">
              <Label htmlFor="current-password">Password saat ini</Label>
              <Input id="current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-password">Password baru</Label>
              <Input id="new-password" type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Konfirmasi password baru</Label>
              <Input id="confirm-password" type="password" minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>

            <Button type="submit" disabled={savingPassword} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPassword ? "Menyimpan..." : "Ubah Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck size={18} /> PIN Keamanan</CardTitle>
          <CardDescription>
            PIN 6 digit dipakai untuk mengonfirmasi aktivasi atau perpanjangan langganan outlet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={savePin} className="space-y-4 max-w-xl">
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm">
              Status PIN: <span className="font-semibold">{hasPin ? "Sudah aktif" : "Belum disetel"}</span>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pin-password">Password saat ini</Label>
              <Input id="pin-password" type="password" value={pinPassword} onChange={(e) => setPinPassword(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-pin">PIN baru</Label>
              <Input
                id="new-pin"
                type="password"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                minLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6 digit angka"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-pin">Konfirmasi PIN</Label>
              <Input
                id="confirm-pin"
                type="password"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                minLength={6}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Ulangi PIN"
                required
              />
            </div>

            <Button type="submit" disabled={savingPin} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPin ? "Menyimpan..." : hasPin ? "Ganti PIN" : "Simpan PIN"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
